import API_ENDPOINTS from '../config/apiConfig';

// ============================================
// SESSION / EXPIRY MANAGEMENT
// ============================================
// Auth tokens live in localStorage rather than sessionStorage so that pages
// opened in a new tab (e.g. the Summary Report opened via window.open) can
// read them. The trade-off is that tokens persist across browser restarts
// until expiry or explicit logout — acceptable here because we have a short
// access-token lifetime and a proactive auto-logout timer.
export const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const EXPIRES_AT_KEY = 'access_token_expires_at';

// Single point that knows where tokens live. If you ever want to migrate
// off localStorage (e.g. to HTTP-only cookies) this is the only place that
// needs to change.
export const tokenStorage = {
    get: (key) => localStorage.getItem(key),
    set: (key, value) => localStorage.setItem(key, value),
    remove: (key) => localStorage.removeItem(key),
};

// Holds the active setTimeout handle for proactive logout. Lives at module
// scope so it persists across renders and can be cleared from anywhere.
let expiryTimerId = null;

/**
 * Wipe all session data and cancel the pending expiry timer.
 * Used by login (to start clean), logout, the 401 handler, and the
 * expiry timer itself — single source of truth for "end the session".
 */
const clearSession = () => {
    tokenStorage.remove(ACCESS_TOKEN_KEY);
    tokenStorage.remove(REFRESH_TOKEN_KEY);
    tokenStorage.remove(EXPIRES_AT_KEY);

    if (expiryTimerId !== null) {
        clearTimeout(expiryTimerId);
        expiryTimerId = null;
    }
};

/**
 * Schedule a proactive logout for when the current access token expires.
 * Reads the stored expiry; if the token is already expired (or no expiry
 * is stored), clears the session immediately. Otherwise sets a timer that
 * will clear tokens and redirect to /login at expiry — even if the user
 * has been idle the entire time.
 *
 * Safe to call repeatedly: any existing timer is cleared first, so this
 * function can also be used to "rearm" the timer after login or on page
 * load.
 */
export const scheduleSessionExpiry = () => {
    if (expiryTimerId !== null) {
        clearTimeout(expiryTimerId);
        expiryTimerId = null;
    }

    const expiresAtRaw = tokenStorage.get(EXPIRES_AT_KEY);
    if (!expiresAtRaw) {
        // No expiry stored — either not logged in, or logged in before
        // expiry tracking existed. Nothing to schedule.
        return;
    }

    const expiresAt = Number(expiresAtRaw);
    const msUntilExpiry = expiresAt - Date.now();

    if (msUntilExpiry <= 0) {
        // Token already expired — clear and redirect now.
        clearSession();
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
        return;
    }

    // setTimeout's max delay is ~24.8 days. Access tokens are ~30 minutes
    // here, so this is well within range.
    expiryTimerId = setTimeout(() => {
        clearSession();
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
    }, msUntilExpiry);
};

// ============================================
// CROSS-TAB SESSION SYNC
// ============================================
// localStorage fires a `storage` event in *other* same-origin tabs whenever
// a key is changed. We listen for the access-token key disappearing (i.e.
// another tab logged out or had its token expire) and force this tab to the
// login screen too. This keeps all open tabs in a consistent auth state.
//
// Note: this event fires only in other tabs, never in the tab that made the
// change. The acting tab handles its own redirect through the normal flow.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        // event.key is null when localStorage.clear() is called wholesale.
        if (event.key !== null && event.key !== ACCESS_TOKEN_KEY) return;

        // If the access token was removed in another tab, mirror the
        // teardown here: cancel our own timer and bounce to /login.
        if (event.newValue === null) {
            if (expiryTimerId !== null) {
                clearTimeout(expiryTimerId);
                expiryTimerId = null;
            }
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
    });
}

/**
 * Base fetch function with common error handling.
 * Automatically attaches the Bearer access token from storage to every
 * request (unless `skipAuth: true` is passed in options).
 * If a 401 is received, clears tokens and redirects to /login.
 *
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options. Pass `skipAuth: true` to skip token attachment (e.g., for login).
 *   Pass `responseType: 'text'` for endpoints that return plain text instead of JSON
 *   (e.g., a PEM-encoded public key). Defaults to 'json'.
 * @returns {Promise<object|string>} - Parsed JSON object, or the raw response text when responseType is 'text'.
 */
const apiFetch = async (url, options = {}) => {
    try {
        const { skipAuth, responseType = 'json', ...fetchOptions } = options;
        const token = tokenStorage.get(ACCESS_TOKEN_KEY);

        const response = await fetch(url, {
            ...fetchOptions,
            headers: {
                'Content-Type': 'application/json',
                ...(!skipAuth && token ? { 'Authorization': `Bearer ${token}` } : {}),
                ...fetchOptions.headers,
            },
        });

        // Handle 401 globally: token expired/invalid -> force logout
        if (response.status === 401 && !skipAuth) {
            clearSession();
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
            throw new Error('Session expired. Please log in again.');
        }

        // Text endpoints (e.g., PEM-encoded public keys) don't return JSON,
        // so we read the body as text and surface non-2xx errors using the
        // text itself as the error message.
        if (responseType === 'text') {
            const text = await response.text();
            if (!response.ok) {
                throw new Error(text || 'API request failed');
            }
            return text;
        }

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data?.detail || data?.message || 'API request failed');
        }

        return data;
    } catch (error) {
        console.error('API Error:', error);
        throw error;
    }
};

// ============================================
// CONNECTION SERVICE
// ============================================
export const connectionService = {

    /**
     * Fetches the server's RSA public key (PEM-encoded plain text).
     * Routed through apiFetch so it inherits the Authorization header,
     * 401 handling, and session-expiry behavior.
     */
    getPublicKey: async () => {
        return apiFetch(API_ENDPOINTS.CONNECTIONS.PUBLIC_KEY, {
            method: 'GET',
            responseType: 'text',
        });
    },

    getAllConnections: async ({ page = 1, limit = 6, search = "" } = {}) => {
        const url = new URL(`${API_ENDPOINTS.CONNECTIONS.GET_ALL}`);
        url.searchParams.append("page", page);
        url.searchParams.append("limit", limit);
        if (search) url.searchParams.append("search", search);

        return apiFetch(url.toString(), { method: 'GET' });
    },

    testConnection: async (connectionData) => {
        return apiFetch(API_ENDPOINTS.CONNECTIONS.TEST, {
            method: 'POST',
            body: JSON.stringify(connectionData),
        });
    },

    addConnection: async (connectionData) => {
        return apiFetch(API_ENDPOINTS.CONNECTIONS.ADD, {
            method: 'POST',
            body: JSON.stringify(connectionData),
        });
    },

    testUpdatedConnection: async (connectionId, connectionData) => {
        return apiFetch(API_ENDPOINTS.CONNECTIONS.TEST_UPDATED(connectionId), {
            method: 'POST',
            body: JSON.stringify(connectionData),
        });
    },

    updateConnection: async (connectionId, connectionData) => {
        return apiFetch(API_ENDPOINTS.CONNECTIONS.UPDATE(connectionId), {
            method: 'PUT',
            body: JSON.stringify(connectionData),
        });
    },

    activateConnection: async (connectionId) => {
        return apiFetch(API_ENDPOINTS.CONNECTIONS.ACTIVATE(connectionId), {
            method: 'POST',
        });
    },

    deactivateConnection: async (connectionId) => {
        return apiFetch(API_ENDPOINTS.CONNECTIONS.DEACTIVATE(connectionId), {
            method: 'POST',
        });
    },

    deleteConnection: async (connectionId) => {
        return apiFetch(API_ENDPOINTS.CONNECTIONS.DELETE(connectionId), {
            method: 'DELETE',
        });
    },
};

// ============================================
// JOB SERVICE
// ============================================
export const jobService = {

    getAllJobs: async ({ page = 1, limit = 6, search = "" } = {}) => {
        const url = new URL(`${API_ENDPOINTS.JOBS.GET_ALL}`);
        url.searchParams.append("page", page);
        url.searchParams.append("limit", limit);
        if (search) url.searchParams.append("search", search);

        return apiFetch(url.toString(), { method: 'GET', cache: 'no-store' });
    },

    getActiveConnections: async () => {
        return apiFetch(API_ENDPOINTS.JOBS.ACTIVE_CONNECTIONS, {
            method: 'GET',
        });
    },

    getSourceAndTargetSchemas: async (connectionIds) => {
        return apiFetch(API_ENDPOINTS.JOBS.SCHEMA_DROPDOWNS, {
            method: 'POST',
            body: JSON.stringify(connectionIds),
        });
    },

    saveJob: async (jobData) => {
        return apiFetch(API_ENDPOINTS.JOBS.SAVE, {
            method: 'POST',
            body: JSON.stringify(jobData),
        });
    },

    saveAndRunJob: async (jobData) => {
        return apiFetch(API_ENDPOINTS.JOBS.SAVE_AND_RUN, {
            method: 'POST',
            body: JSON.stringify(jobData),
        });
    },

    updateJob: async (jobId, jobData) => {
        return apiFetch(API_ENDPOINTS.JOBS.UPDATE(jobId), {
            method: 'PUT',
            body: JSON.stringify(jobData),
        });
    },

    updateJobAndRun: async (jobId, jobData) => {
        return apiFetch(API_ENDPOINTS.JOBS.UPDATE_AND_RUN(jobId), {
            method: 'PUT',
            body: JSON.stringify(jobData),
        });
    },

    deleteJob: async (jobId) => {
        return apiFetch(API_ENDPOINTS.JOBS.DELETE(jobId), {
            method: 'DELETE',
        });
    },

    startJob: async (jobId) => {
        return apiFetch(API_ENDPOINTS.JOBS.START(jobId), {
            method: 'POST',
        });
    },

    stopJob: async (jobId) => {
        return apiFetch(API_ENDPOINTS.JOBS.STOP(jobId), {
            method: 'POST',
        });
    },

    getRunById: async (runId) => {
        return apiFetch(API_ENDPOINTS.JOBS.GET_RUN(runId), {
            method: 'GET',
        });
    },

    getSummaryReport: async (runId) => {
        return apiFetch(API_ENDPOINTS.JOBS.GET_REPORT(runId), {
            method: 'GET',
        });
    },

    getActiveGitConnections: async () => {
        return apiFetch(API_ENDPOINTS.JOBS.ACTIVE_GIT_CONNECTIONS, {
            method: 'GET',
        });
    },

};

// ============================================
// REPOSITORY SERVICE
// ============================================
export const repositoryService = {

    getAllRepos: async ({ page = 1, limit = 6, search = "" } = {}) => {
        const url = new URL(`${API_ENDPOINTS.REPOSITORIES.GET_ALL}`);
        url.searchParams.append("page", page);
        url.searchParams.append("limit", limit);
        if (search) url.searchParams.append("search", search);

        return apiFetch(url.toString(), { method: 'GET' });
    },

    getRepoValues: async (repoData) => {
        return apiFetch(API_ENDPOINTS.REPOSITORIES.REPOS_DROPDOWN, {
            method: 'POST',
            body: JSON.stringify(repoData),
        });
    },

    getBranchValues: async (repoData) => {
        return apiFetch(API_ENDPOINTS.REPOSITORIES.BRANCHES_DROPDOWN, {
            method: 'POST',
            body: JSON.stringify(repoData),
        });
    },

    addRepository: async (repoData) => {
        return apiFetch(API_ENDPOINTS.REPOSITORIES.ADD, {
            method: 'POST',
            body: JSON.stringify(repoData),
        });
    },

    updateRepo: async (repoId, repoData) => {
        return apiFetch(API_ENDPOINTS.REPOSITORIES.UPDATE(repoId), {
            method: 'PUT',
            body: JSON.stringify(repoData),
        });
    },

    deleteRepo: async (repoId) => {
        return apiFetch(API_ENDPOINTS.REPOSITORIES.DELETE(repoId), {
            method: 'DELETE',
        });
    },

    pushToRepo: async (repoId, repoData) => {
        return apiFetch(API_ENDPOINTS.REPOSITORIES.PUSH_TO_GIT(repoId), {
            method: 'POST',
            body: JSON.stringify(repoData),
        });
    },
}

// ============================================
// USER SERVICE
// ============================================
// export const userService = {

//     getAllUsers: async (params = {}) => {
//         const url = new URL(API_ENDPOINTS.USERS.GET_ALL);
//         Object.keys(params).forEach(key => {
//             if (params[key]) url.searchParams.append(key, params[key]);
//         });

//         return apiFetch(url.toString(), {
//             method: 'GET',
//         });
//     },

//     createUser: async (userData) => {
//         return apiFetch(API_ENDPOINTS.USERS.CREATE, {
//             method: 'POST',
//             body: JSON.stringify(userData),
//         });
//     },


//     updateUser: async (userId, userData) => {
//         return apiFetch(API_ENDPOINTS.USERS.UPDATE(userId), {
//             method: 'PUT',
//             body: JSON.stringify(userData),
//         });
//     },


//     deleteUser: async (userId) => {
//         return apiFetch(API_ENDPOINTS.USERS.DELETE(userId), {
//             method: 'DELETE',
//         });
//     },


//     activateUser: async (userId) => {
//         return apiFetch(API_ENDPOINTS.USERS.ACTIVATE(userId), {
//             method: 'POST',
//         });
//     },


//     deactivateUser: async (userId) => {
//         return apiFetch(API_ENDPOINTS.USERS.DEACTIVATE(userId), {
//             method: 'POST',
//         });
//     },

//     changePassword: async (userId, passwordData) => {
//         return apiFetch(API_ENDPOINTS.USERS.CHANGE_PASSWORD(userId), {
//             method: 'POST',
//             body: JSON.stringify(passwordData),
//         });
//     },

//     resetPassword: async (userId) => {
//         return apiFetch(API_ENDPOINTS.USERS.RESET_PASSWORD(userId), {
//             method: 'POST',
//         });
//     },
// };

// ============================================
// AUTHENTICATION SERVICE
// ============================================
export const authService = {

    login: async (credentials) => {
        // skipAuth: true so we don't try to attach a (nonexistent) token to the login call
        const data = await apiFetch(API_ENDPOINTS.AUTH.LOGIN, {
            method: 'POST',
            body: JSON.stringify(credentials),
            skipAuth: true,
        });

        // Persist tokens for subsequent authenticated requests
        if (data?.access_token) {
            tokenStorage.set(ACCESS_TOKEN_KEY, data.access_token);
        }
        if (data?.refresh_token) {
            tokenStorage.set(REFRESH_TOKEN_KEY, data.refresh_token);
        }

        // Persist absolute expiry timestamp so we can detect a stale session
        // without hitting the network. `expires_in` is in seconds.
        if (data?.expires_in) {
            const expiresAt = Date.now() + data.expires_in * 1000;
            tokenStorage.set(EXPIRES_AT_KEY, String(expiresAt));
        }

        // Arm the proactive auto-logout timer for this session.
        scheduleSessionExpiry();

        return data;
    },


    logout: async () => {
        try {
            await apiFetch(API_ENDPOINTS.AUTH.LOGOUT, {
                method: 'POST',
            });
        } catch (error) {
            // Even if the server-side logout fails, we still want to clear
            // the client-side session so the user isn't trapped.
            console.error('Logout API failed; clearing session anyway.', error);
        } finally {
            clearSession();
        }
    },

    isAuthenticated: () => {
        const token = tokenStorage.get(ACCESS_TOKEN_KEY);
        if (!token) return false;

        // If we have an expiry stored, treat an already-expired token as
        // unauthenticated — and clean up so a stale token can't keep
        // passing this check on subsequent calls.
        const expiresAtRaw = tokenStorage.get(EXPIRES_AT_KEY);
        if (expiresAtRaw) {
            const expiresAt = Number(expiresAtRaw);
            if (Date.now() >= expiresAt) {
                clearSession();
                return false;
            }
        }

        return true;
    },


    // refreshToken: async (refreshToken) => {
    //     return apiFetch(API_ENDPOINTS.AUTH.REFRESH_TOKEN, {
    //         method: 'POST',
    //         body: JSON.stringify({ refresh_token: refreshToken }),
    //     });
    // },


    // verifyToken: async (token) => {
    //     return apiFetch(API_ENDPOINTS.AUTH.VERIFY_TOKEN, {
    //         method: 'POST',
    //         body: JSON.stringify({ token }),
    //     });
    // },
};

// // Export all services as default
// export default {
//     connection: connectionService,
//     job: jobService,
//     user: userService,
//     auth: authService,
// };